// @ts-nocheck
/**
 * chartRenderer.ts — Template V2 inline chart component renderer (DEC-056).
 *
 * Modes: DEC-078 list/matrix `chart.source`, table/fileTable columns, or xSource/ySource.
 * Multi-series XY via chart.series (max 8); legacy yColumn/ySource migrate to series[0].
 * Explicit limits are inclusive data filters; omit = Auto.
 */

import type {
  ChartOrientation,
  ChartSeriesDef,
  LayoutChartType,
} from '../../types/component';
import type { TablePropertySchema } from '../../types/category';
import type { ValueSource } from '../../types/binding';
import type { LocalizedText } from '../../types/metadata';
import type { PlotSeries } from '../../../../../shims/appStub';
import { getColumnValuesAllowingItemRefInt } from '../table/tableCodec';
import { looksLikeFileTableManifestValue } from '../table/fileTableManifest';
import type { LayoutChartSeriesPoint } from './chartSeriesFromRows';
import {
  buildMultiSeriesPlot,
  buildPlottedSeries,
  normalizeSourceToList,
  type MultiSeriesYInput,
} from './chartSeriesPipeline';
import {
  MAX_CHART_SERIES,
  migrateLayoutChartType,
  XY_MULTI_SERIES_CHART_TYPES,
} from './chartTypeMigration';
import { seriesFromListValue } from './listChartCoercion';
import { applyExpressionFallback, buildDescriptor, coerceResolvedDisplayValue, resolveDisplayString, resolveValue } from './rendererUtils';
import type { CompiledPropsByType, ComponentRenderContext, ComponentRenderer } from './types';
import { extractTypedProps } from './types';

export type { LayoutChartSeriesPoint };

/** Resolved, serializable per-series chrome for descriptor + fileTable hydration. */
export type ChartSeriesMeta = {
  id: string;
  yColumn?: string;
  label?: string;
  color?: string;
  strokeWidth?: number;
  yAxis: 'primary' | 'secondary';
  yMin?: number;
  yMax?: number;
};

function resolveChartBindingMeta(
  context: Parameters<ComponentRenderer<CompiledPropsByType['chart']>['render']>[0],
  inlineSchema: TablePropertySchema | undefined,
  tableSource: ValueSource<string> | undefined,
): {
  schema: TablePropertySchema | undefined;
  storageKind: 'ctbl' | 'fileTable';
  propertyId?: string;
  isTableProperty: boolean;
} {
  if (tableSource?.kind === 'binding' && tableSource.ref.kind === 'categoryProperty') {
    const ref = tableSource.ref;
    const category = context.compiledTemplate.categoriesById[ref.categoryId];
    const property = category?.properties.find((prop) => prop.id === ref.propertyId);
    const schema =
      inlineSchema ?? (property?.validation?.tableSchema as TablePropertySchema | undefined);
    const isTableProperty =
      property?.type === 'table' || property?.type === 'fileTable';
    const storageKind = property?.type === 'fileTable' ? 'fileTable' : 'ctbl';
    return { schema, storageKind, propertyId: ref.propertyId, isTableProperty };
  }
  if (inlineSchema) {
    return {
      schema: inlineSchema,
      storageKind: 'ctbl',
      isTableProperty: true,
    };
  }
  return {
    schema: undefined,
    storageKind: 'ctbl',
    isTableProperty: false,
  };
}

function columnHeaderLabel(
  schema: TablePropertySchema,
  columnId: string | undefined,
  locale: string | undefined,
): string | undefined {
  if (!columnId) return undefined;
  const col = schema.columns.find((c) => c.id === columnId);
  if (!col) return undefined;
  return resolveDisplayString(col.headerName, locale, col.id);
}

function extractColumnOrWarn(
  context: Parameters<ComponentRenderer<CompiledPropsByType['chart']>['render']>[0],
  base64: string,
  schema: TablePropertySchema,
  columnId: string,
  fieldPath: string,
): Array<string | number | null> | null {
  const columnIndex = schema.columns.findIndex((c) => c.id === columnId);
  if (columnIndex < 0) {
    context.issues.push({
      severity: 'warning',
      code: 'schema.invalid_field_type',
      message: `Chart column "${columnId}" not found for node "${context.node.id}"`,
      path: `${context.node.id}.${fieldPath}`,
    });
    return null;
  }
  if (!base64) {
    return [];
  }
  const cells = getColumnValuesAllowingItemRefInt(base64, schema, columnIndex);
  if (!cells.ok) {
    context.issues.push({
      severity: 'warning',
      code: 'schema.invalid_field_type',
      message: `Chart could not read column "${columnId}" for node "${context.node.id}": ${cells.message}`,
      path: `${context.node.id}.${fieldPath}`,
    });
    return null;
  }
  return cells.value.map((v) => (v == null ? null : v));
}

function resolveOptionalNumber(
  source: ValueSource<number> | undefined,
  context: ComponentRenderContext<unknown>,
): number | undefined {
  if (!source) return undefined;
  const raw = resolveValue(source, context);
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined;
}

function resolveOptionalStringSource(
  source: ValueSource<string> | ValueSource<LocalizedText> | undefined,
  context: ComponentRenderContext<unknown>,
): string | undefined {
  if (!source) return undefined;
  const raw = coerceResolvedDisplayValue(resolveValue(source, context), context.locale);
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function resolveChartType(
  raw: ValueSource<LayoutChartType> | LayoutChartType | undefined,
  legacySeriesMode: string | undefined,
  context: ComponentRenderContext<unknown>,
): LayoutChartType {
  let token: string | undefined;
  if (typeof raw === 'string') {
    token = raw;
  } else if (raw && typeof raw === 'object' && 'kind' in raw) {
    const resolved = resolveValue(raw as ValueSource<LayoutChartType>, context);
    if (typeof resolved === 'string') token = resolved;
  }
  return migrateLayoutChartType(token, legacySeriesMode);
}

function resolveSeriesDefs(
  p: CompiledPropsByType['chart']['chart'],
): ChartSeriesDef[] {
  if (Array.isArray(p.series) && p.series.length > 0) {
    return p.series.slice(0, MAX_CHART_SERIES);
  }
  const legacy: ChartSeriesDef = { id: 'y' };
  const yColumn = p.yColumn?.trim();
  if (yColumn) legacy.yColumn = yColumn;
  if (p.ySource) legacy.ySource = p.ySource;
  if (p.seriesColor) legacy.color = p.seriesColor;
  if (p.yMin) legacy.yMin = p.yMin;
  if (p.yMax) legacy.yMax = p.yMax;
  return [legacy];
}

function resolveSeriesMeta(
  seriesDefs: ChartSeriesDef[],
  chartType: LayoutChartType,
  p: CompiledPropsByType['chart']['chart'],
  context: ComponentRenderContext<unknown>,
  defaultStrokeWidth: number,
  topLevelSeriesColor: string | undefined,
): ChartSeriesMeta[] {
  const legacyYMin = resolveOptionalNumber(p.yMin, context);
  const legacyYMax = resolveOptionalNumber(p.yMax, context);
  return seriesDefs.map((def, index) => {
    const yAxis: 'primary' | 'secondary' =
      XY_MULTI_SERIES_CHART_TYPES.has(chartType) && def.yAxis === 'secondary'
        ? 'secondary'
        : 'primary';
    const yColumn = def.yColumn?.trim() || undefined;
    return {
      id: typeof def.id === 'string' && def.id.length > 0 ? def.id : `s${index}`,
      yColumn,
      label: resolveOptionalStringSource(def.label, context),
      color:
        resolveOptionalStringSource(def.color, context) ??
        (index === 0 ? topLevelSeriesColor : undefined),
      strokeWidth: resolveOptionalNumber(def.strokeWidth, context) ?? defaultStrokeWidth,
      yAxis,
      yMin:
        resolveOptionalNumber(def.yMin, context) ??
        (index === 0 ? legacyYMin : undefined),
      yMax:
        resolveOptionalNumber(def.yMax, context) ??
        (index === 0 ? legacyYMax : undefined),
    };
  });
}

export const CHART_RENDERER: ComponentRenderer<CompiledPropsByType['chart']> = {
  supportedType: 'chart',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    const p = extractTypedProps('chart', context.node.props).chart;
    const legacySeriesMode =
      (p as { seriesMode?: string }).seriesMode === 'frequencies'
        ? 'frequencies'
        : undefined;
    const chartType = resolveChartType(p.chartType as never, legacySeriesMode, context);
    const orientation: ChartOrientation =
      p.orientation === 'horizontal' ? 'horizontal' : 'vertical';
    const binCount = resolveOptionalNumber(p.binCount, context) ?? 10;

    const xMin = resolveOptionalNumber(p.xMin, context);
    const xMax = resolveOptionalNumber(p.xMax, context);

    const seriesDefs = resolveSeriesDefs(p);
    const defaultStrokeWidth = resolveOptionalNumber(p.strokeWidth, context) ?? 2;
    const topLevelSeriesColor = resolveOptionalStringSource(p.seriesColor, context);

    const listSourceRaw =
      p.source != null ? resolveValue(p.source as ValueSource<unknown>, context) : undefined;
    const useListBackedSource = Array.isArray(listSourceRaw);

    let seriesMeta = resolveSeriesMeta(
      seriesDefs,
      chartType,
      p,
      context,
      defaultStrokeWidth,
      topLevelSeriesColor,
    );

    const meta = resolveChartBindingMeta(context, p.schema, p.tableSource);
    const schema = meta.schema;

    let xValues: Array<string | number | null> = [];
    let seriesYValues: Array<Array<string | number | null>> = seriesMeta.map(() => []);
    let labelValues: Array<string | number | null> | null = null;
    let colorValues: Array<string | number | null> | null = null;
    let storageKind: 'ctbl' | 'fileTable' | 'lists' | 'listSource' = 'lists';
    let storedValue = '';
    let useTablePath = false;
    let listBackedPlotSeries: PlotSeries[] | null = null;

    let xAxisLabel = resolveOptionalStringSource(p.xAxisLabel, context);
    let yAxisLabel = resolveOptionalStringSource(p.yAxisLabel, context);
    const ySecondaryAxisLabel = resolveOptionalStringSource(p.ySecondaryAxisLabel, context);

    if (useListBackedSource) {
      storageKind = 'listSource';
      if (p.tableSource != null || p.xSource != null || seriesDefs.some((d) => d.ySource != null)) {
        context.issues.push({
          severity: 'warning',
          code: 'schema.invalid_field_type',
          message: `Chart node "${context.node.id}": list-backed chart.source is set; tableSource / xSource / ySource data binds are ignored.`,
          path: `${context.node.id}.chart.source`,
        });
      }
      const coerced = seriesFromListValue(
        listSourceRaw,
        chartType,
        seriesDefs.map((d) => ({
          id: typeof d.id === 'string' && d.id.length > 0 ? d.id : 'y',
          yAxis: d.yAxis,
        })),
      );
      for (const issue of coerced.issues) {
        context.issues.push({
          severity: issue.severity,
          code: issue.code,
          message: `Chart node "${context.node.id}": ${issue.message}`,
          path: `${context.node.id}.chart.source`,
        });
      }
      // Expand authored meta to coerced series length (synthesize extras on primary).
      const expandedDefs: ChartSeriesDef[] = coerced.series.map((s, i) => {
        const base = seriesDefs[i];
        if (base) {
          return { ...base, id: s.id, yAxis: s.yAxis };
        }
        return { id: s.id, yAxis: 'primary' as const };
      });
      seriesMeta = resolveSeriesMeta(
        expandedDefs,
        chartType,
        p,
        context,
        defaultStrokeWidth,
        topLevelSeriesColor,
      );
      seriesYValues = coerced.series.map((s) => s.yList);
      xValues = coerced.series[0]?.xList ?? [];

      listBackedPlotSeries = coerced.series.map((s, index) => {
        const m = seriesMeta[index]!;
        const points = buildPlottedSeries({
          chartType,
          xValues: s.xList,
          yValues: s.yList,
          labelValues: index === 0 ? labelValues : null,
          colorValues: index === 0 ? colorValues : null,
          limits: {
            xMin,
            xMax,
            yMin: m.yMin,
            yMax: m.yMax,
          },
          binCount,
        });
        return {
          id: m.id || s.id,
          label: m.label,
          color: m.color ?? (index === 0 ? topLevelSeriesColor : undefined),
          strokeWidth: m.strokeWidth,
          yAxis: s.yAxis,
          points,
        };
      });
      // Non-XY categorical charts without authored extra series stay single-plot.
      // If the template authored 2+ series, keep them (dual plots must survive bake).
      if (
        !XY_MULTI_SERIES_CHART_TYPES.has(chartType) &&
        listBackedPlotSeries.length > 1 &&
        seriesDefs.length <= 1
      ) {
        listBackedPlotSeries = listBackedPlotSeries.slice(0, 1);
      }
    } else if (p.tableSource && meta.isTableProperty && schema) {
      useTablePath = true;
      const raw = resolveValue(p.tableSource, context);
      storedValue = typeof raw === 'string' ? raw : '';
      storageKind =
        meta.storageKind === 'fileTable' || looksLikeFileTableManifestValue(storedValue)
          ? 'fileTable'
          : 'ctbl';

      if (storageKind === 'ctbl') {
        const hasAnyYColumn = seriesMeta.some((s) => s.yColumn);
        if (!hasAnyYColumn) {
          context.issues.push({
            severity: 'warning',
            code: 'schema.missing_required_field',
            message: `Chart yColumn missing for node "${context.node.id}"`,
            path: `${context.node.id}.chart.series`,
          });
        } else {
          if (p.xColumn?.trim()) {
            const extractedX = extractColumnOrWarn(
              context,
              storedValue,
              schema,
              p.xColumn.trim(),
              'chart.xColumn',
            );
            if (extractedX) xValues = extractedX;
          }

          for (let i = 0; i < seriesMeta.length; i += 1) {
            const s = seriesMeta[i]!;
            const yColumn = s.yColumn;
            if (!yColumn) continue;
            const extractedY = extractColumnOrWarn(
              context,
              storedValue,
              schema,
              yColumn,
              `chart.series[${i}].yColumn`,
            );
            if (extractedY) seriesYValues[i] = extractedY;
          }

          // labelColumn / colorColumn only for first series (categorical / single-series).
          if (p.labelColumn) {
            labelValues = extractColumnOrWarn(
              context,
              storedValue,
              schema,
              p.labelColumn,
              'chart.labelColumn',
            );
          }
          if (p.colorColumn) {
            colorValues = extractColumnOrWarn(
              context,
              storedValue,
              schema,
              p.colorColumn,
              'chart.colorColumn',
            );
          }
        }

        if (!xAxisLabel) xAxisLabel = columnHeaderLabel(schema, p.xColumn, context.locale);
        if (!yAxisLabel) yAxisLabel = columnHeaderLabel(schema, seriesMeta[0]?.yColumn, context.locale);
      } else {
        if (!xAxisLabel) xAxisLabel = columnHeaderLabel(schema, p.xColumn, context.locale);
        if (!yAxisLabel) yAxisLabel = columnHeaderLabel(schema, seriesMeta[0]?.yColumn, context.locale);
      }
    } else {
      storageKind = 'lists';
      if (p.xSource) {
        xValues = normalizeSourceToList(resolveValue(p.xSource as ValueSource<unknown>, context));
      }
      for (let i = 0; i < seriesDefs.length; i += 1) {
        const def = seriesDefs[i]!;
        if (def.ySource) {
          seriesYValues[i] = normalizeSourceToList(
            resolveValue(def.ySource as ValueSource<unknown>, context),
          );
        }
      }
    }

    let plotSeries: PlotSeries[] = [];
    if (listBackedPlotSeries) {
      plotSeries = listBackedPlotSeries;
    } else if (!(useTablePath && storageKind === 'fileTable')) {
      const seriesInputs: MultiSeriesYInput[] = seriesMeta.map((s, i) => ({
        id: s.id,
        yValues: seriesYValues[i] ?? [],
        label: s.label,
        color: s.color,
        strokeWidth: s.strokeWidth,
        yAxis: s.yAxis,
        yMin: s.yMin,
        yMax: s.yMax,
      }));
      plotSeries = buildMultiSeriesPlot({
        chartType,
        xValues,
        seriesInputs,
        labelValues,
        colorValues,
        xMin,
        xMax,
        binCount,
      });
    }

    // Flat first-series points for backward-compatible consumers.
    const series: LayoutChartSeriesPoint[] = plotSeries[0]?.points ?? [];
    const firstMeta = seriesMeta[0];

    const title = resolveOptionalStringSource(p.title, context);
    const axisColor = resolveOptionalStringSource(p.axisColor, context);

    return buildDescriptor(
      context.node,
      visible,
      enabled,
      {
        chartType,
        orientation,
        binCount,
        plotSeries,
        series,
        title,
        xAxisLabel,
        yAxisLabel,
        ySecondaryAxisLabel,
        xMin,
        xMax,
        yMin: firstMeta?.yMin,
        yMax: firstMeta?.yMax,
        strokeWidth: defaultStrokeWidth,
        seriesColor: firstMeta?.color ?? topLevelSeriesColor,
        axisColor,
        xColumn: p.xColumn,
        yColumn: firstMeta?.yColumn ?? p.yColumn,
        labelColumn: p.labelColumn,
        colorColumn: p.colorColumn,
        seriesMeta,
        storageKind,
        storedValue,
        schema,
        propertyId: meta.propertyId,
        limitsAreFilters: true,
      },
      { diagnostics: context.issues.slice(issuesBefore), renderContext: context },
    );
  },
};
